import React from 'react';
import { classNames } from '../../utils/helpers';

const Input = React.forwardRef(
  ({ 
    label, 
    error, 
    helperText, 
    className = '',
    fullWidth = true,
    leftIcon,
    rightIcon,
    variant = 'default',
    ...props 
  }, ref) => {
    const baseInputClasses = 'block rounded-xl transition-all duration-300 focus:scale-[1.01] placeholder:text-gray-400';
    
    const variantClasses = {
      default: `${baseInputClasses} border-white/10 bg-white/5 backdrop-blur-md shadow-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50 hover:border-white/20 text-white`,
      glass: `${baseInputClasses} border-white/20 bg-white/10 backdrop-blur-xl shadow-lg focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/50 text-white placeholder:text-gray-300`,
      filled: `${baseInputClasses} border-0 bg-gray-100 hover:bg-gray-200 focus:bg-white shadow-md focus:ring-2 focus:ring-cyan-500/50 text-gray-900`,
      dark: `${baseInputClasses} border-white/10 bg-slate-800/50 backdrop-blur-xl shadow-lg focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/50 text-white placeholder:text-gray-400`,
    };

    const inputClasses = classNames(
      variantClasses[variant],
      'sm:text-sm px-4 py-3',
      error && 'border-red-400 focus:border-red-500 focus:ring-red-500/50 bg-red-50/50',
      leftIcon && 'pl-11',
      rightIcon && 'pr-11',
      fullWidth && 'w-full',
      className
    );

    const labelClasses = classNames(
      'block text-sm font-semibold mb-2',
      variant === 'glass' || variant === 'dark' ? 'text-white' : 'text-gray-700'
    );

    return (
      <div className={fullWidth ? 'w-full' : ''}>
        {label && (
          <label className={labelClasses}>
            {label}
          </label>
        )}
        <div className="relative group">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {typeof leftIcon === 'string' ? (
                <span className="text-gray-400 text-lg">{leftIcon}</span>
              ) : (
                <div className="text-gray-400">{leftIcon}</div>
              )}
            </div>
          )}
          <input
            ref={ref}
            className={inputClasses}
            {...props}
          />
          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              {typeof rightIcon === 'string' ? (
                <span className="text-gray-400 text-lg">{rightIcon}</span>
              ) : (
                <div className="text-gray-400">{rightIcon}</div>
              )}
            </div>
          )}
        </div>
        {error && (
          <p className="mt-2 text-sm text-red-500 flex items-center gap-1 font-medium">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className={classNames(
            'mt-2 text-sm',
            variant === 'glass' || variant === 'dark' ? 'text-gray-300' : 'text-gray-500'
          )}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;